import { ChangeEvent, KeyboardEventHandler, useCallback, useMemo, useRef } from 'react';
import styles from './SearchLayout.module.scss'
import Button from 'src/components/button/Button';
import SearchIcon from 'src/assets/icons/new_search.svg?react';
import ChevronDown from 'src/assets/icons/dropdown.svg?react';
import RewardIcon from 'src/assets/icons/grade_reward.svg?react';
import TierIcon from 'src/assets/icons/user_icon.svg?react';
import LampIcon from 'src/assets/icons/lamp_icon.svg?react';
import IconEmpty from 'src/assets/icons/checkbox.svg?react';
import IconFill from 'src/assets/icons/checkbox_fill.svg?react';
import { Dropdown } from "antd";
import type { MenuProps } from 'antd';
import { SelectOption } from '@src/types';
import { LordIcon } from '@src/components/lord-icon/lord-icon';
import { categoryMoodOptions1, categorySkillsOptions, IconLessons } from '@src/constants';

type Props = {
  focused: boolean;
  setFocused: (value: boolean) => void;
  showGradeMenu: boolean;
  setShowGradeMenu: (value: boolean) => void;
  showLessonTypeMenu: boolean;
  setShowLessonTypeMenu: (value: boolean) => void;
  showTierMenu: boolean;
  setShowTierMenu: (value: boolean) => void;
  showCompetencyMenu: boolean;
  setShowCompetencyMenu: (value: boolean) => void;
  search: string;
  filters: string[];
  filterOptions?: string[];
  selGrades: string[] | [];
  gradeOptions: SelectOption[];
  handleSelectGrade: (grades: string[]) => void;
  selLessonTypes: string[] | [];
  lessonTypeOptions: SelectOption[];
  handleSelectLessonType: (types: string[]) => void;
  selTiers: string[] | [];
  tierOptions: SelectOption[];
  handleSelectTier: (tiers: string[]) => void;
  selCompetencys: string[] | [];
  competencyOptions: SelectOption[];
  handleSelectCompetency: (competencies: string[]) => void;
  handleChangeKeyword: (value: string) => void;
  handleApplySearch: (value?: string) => void;
  isAUuser: boolean;
  showSkillMenu: boolean;
  setShowSkillMenu: (value: boolean) => void;
  showMoodMenu: boolean;
  setShowMoodMenu: (value: boolean) => void;
  selSkill: string[] | [];
  handleSelectSkills: (skills: string[]) => void;
  selMood: string[] | [];
  handleSelectMood: (mood: string[]) => void;
  /** Example searches shown under the bar. Choosing one fills the keyword; it does not search. */
  suggestions?: string[];
}

function SearchLayout(props: Props) {
  const {
    setFocused,
    showGradeMenu,
    setShowGradeMenu,
    showLessonTypeMenu,
    setShowLessonTypeMenu,
    showTierMenu,
    setShowTierMenu,
    showCompetencyMenu,
    setShowCompetencyMenu,
    search,
    selGrades=[],
    gradeOptions,
    selLessonTypes = [],
    lessonTypeOptions,
    selTiers=[],
    tierOptions,
    selCompetencys=[],
    competencyOptions,
    handleSelectGrade,
    handleSelectLessonType,
    handleSelectTier,
    handleSelectCompetency,
    handleChangeKeyword,
    handleApplySearch,
    isAUuser,
    showSkillMenu,
    setShowSkillMenu,
    showMoodMenu,
    setShowMoodMenu,
    selSkill,
    handleSelectSkills,
    selMood,
    handleSelectMood,
    suggestions,
  } = props

  const inputRef = useRef<HTMLInputElement>(null);

  const handleFocus = () => setFocused(true);
  const handleBlur = () => setFocused(false);
  // const inFocus = () => {(inputRef as any).current.focus()};

  const handleGradeDropdownVisible = useCallback((visible: boolean) => {
    setShowGradeMenu(visible);
    setShowTierMenu(false);
    setShowCompetencyMenu(false);
    setShowLessonTypeMenu(false);
  }, [setShowGradeMenu, setShowCompetencyMenu, setShowTierMenu, setShowLessonTypeMenu]);

  const handleLessonTypeDropdownVisible = useCallback((visible: boolean) => {
    setShowLessonTypeMenu(visible);
    setShowGradeMenu(false);
    setShowTierMenu(false);
    setShowCompetencyMenu(false);
  }, [setShowGradeMenu, setShowCompetencyMenu, setShowTierMenu, setShowLessonTypeMenu]);

  const handleTierDropdownVisible = useCallback((visible: boolean) => {
    setShowTierMenu(visible);
    setShowCompetencyMenu(false);
    setShowGradeMenu(false);
    setShowLessonTypeMenu(false);
  }, [setShowGradeMenu, setShowCompetencyMenu, setShowTierMenu, setShowLessonTypeMenu]);

  const handleCompetencyDropdownVisible = useCallback((visible: boolean) => {
    setShowCompetencyMenu(visible);
    setShowTierMenu(false);
    setShowGradeMenu(false);
    setShowLessonTypeMenu(false);
  }, [setShowGradeMenu, setShowCompetencyMenu, setShowTierMenu, setShowLessonTypeMenu]);

  const handleSkillDropdownVisible = useCallback((visible: boolean) => {
    setShowSkillMenu(visible);
    setShowMoodMenu(false);
    setShowGradeMenu(false);
  }, [setShowGradeMenu, setShowMoodMenu, setShowSkillMenu]);

  const handleMoodDropdownVisible = useCallback((visible: boolean) => {
    setShowMoodMenu(visible);
    setShowSkillMenu(false);
    setShowGradeMenu(false);
  }, [setShowGradeMenu, setShowMoodMenu, setShowSkillMenu]);

  const selectGrade: MenuProps['onClick'] = ({ key }) => {
    const index = selGrades.findIndex(v => v === gradeOptions[key].label);
    if (index === -1) {
      handleSelectGrade([
        ...selGrades,
        gradeOptions[key].label,
      ]);
    } else {
      handleSelectGrade(selGrades.filter(v => v !== gradeOptions[key].label));
    }
  };

  const selectLessonType: MenuProps['onClick'] = ({ key }) => {
    const index = selLessonTypes.findIndex(v => v === lessonTypeOptions[key].value);
    if (index === -1) {
      handleSelectLessonType([
        ...selLessonTypes,
        lessonTypeOptions[key].value,
      ]);
    } else {
      handleSelectLessonType(selLessonTypes.filter(v => v !== lessonTypeOptions[key].value));
    }
  };

  const selectTier: MenuProps['onClick'] = ({ key }) => {
    const index = selTiers.findIndex(v => v === tierOptions[key].label);
    if (index === -1) {
      handleSelectTier([
        ...selTiers,
        tierOptions[key].label,
      ]);
    } else {
      handleSelectTier(selTiers.filter(v => v !== tierOptions[key].label));
    }
  };

  const selectCompetency: MenuProps['onClick'] = ({ key }) => {
    const index = selCompetencys.findIndex(v => v === competencyOptions[key].label);
    if (index === -1) {
      handleSelectCompetency([
        ...selCompetencys,
        competencyOptions[key].label,
      ]);
    } else {
      handleSelectCompetency(selCompetencys.filter(v => v !== competencyOptions[key].label));
    }
  };

  const selectSkill: MenuProps['onClick'] = ({ key }) => {
    const index = selSkill.findIndex(v => v === categorySkillsOptions[key].label);
    if (index === -1) {
      handleSelectSkills([
        ...selSkill,
        categorySkillsOptions[key].label,
      ]);
    } else {
      handleSelectSkills(selSkill.filter(v => v !== categorySkillsOptions[key].label));
    }
  };

  const selectMood: MenuProps['onClick'] = ({ key }) => {
    const index = selMood.findIndex(v => v === categoryMoodOptions1[key].label);
    if (index === -1) {
      handleSelectMood([
        ...selMood,
        categoryMoodOptions1[key].label,
      ]);
    } else {
      handleSelectMood(selMood.filter(v => v !== categoryMoodOptions1[key].label));
    }
  };

  const gradeMenu = useMemo(()=> {
    const items: MenuProps['items'] = gradeOptions.map((gradeLevel, index) => {
      const selected = selGrades.length > 0 && selGrades.find((c) => gradeLevel.label === c);
      return {
        key: index,
        label: (
        <div className={`${styles.gradeDropDownMenuitem} ${selected ? styles.select : ''}`}>
          {selected ? <IconFill/> : <IconEmpty/>}
          {gradeLevel.label}
        </div>
        )
      }
    })
    return items
  }, [selGrades, gradeOptions])

  const lessonTypeMenu = useMemo(()=> {
    const items: MenuProps['items'] = lessonTypeOptions.map((type, index) => {
      const selected = selLessonTypes.length && selLessonTypes.find((c) => type.value === c);
      return {
        key: index,
        label: (
        <div className={`${styles.gradeDropDownMenuitem} ${selected ? styles.select : ''}`}>
          {selected ? <IconFill/> : <IconEmpty/>}
          {type.label}
        </div>
        )
      }
    })
    return items
  }, [selLessonTypes, lessonTypeOptions])

  const tierMenu = useMemo(( )=> {
    const items: MenuProps['items'] = (tierOptions || []).map((t, index) => {
      const selected = selTiers.length > 0 && selTiers.find((c) => c === t.label);
      return {
        key: index,
        label: (
        <div className={`${styles.gradeDropDownMenuitem} ${selected ? styles.select : ''}`}>
          {selected ? <IconFill/> : <IconEmpty/>}
          {t.label}
        </div>
        )
      }
    })
    return items
  }, [selTiers, tierOptions])

  const competencyMenu = useMemo(( )=> {
    const items: MenuProps['items'] = (competencyOptions || []).map((comp, index) => {
      const selected = selCompetencys.length > 0 && selCompetencys.find((c) => c === comp.label);
      return {
        key: index,
        label: (
        <div className={`${styles.gradeDropDownMenuitem} ${selected ? styles.select : ''}`}>
            {selected ? <IconFill/> : <IconEmpty/>}
            <span>{comp.label}</span>
        </div>
        )
      }
    })
    return items
  }, [selCompetencys, competencyOptions])

  const skillMenu = useMemo(( )=> {
    const items: MenuProps['items'] = (categorySkillsOptions || []).sort((a, b) => a.label.localeCompare(b.label)).map((comp, index) => {
      const selected = selSkill.length > 0 && selSkill.find((c) => c === comp.label);
      return {
        key: index,
        label: (
        <div className={`${styles.gradeDropDownMenuitem} ${selected ? styles.select : ''}`}>
            {selected ? <IconFill/> : <IconEmpty/>}
            <span>{comp.label}</span>
        </div>
        )
      }
    })
    return items
  }, [selSkill])

  const moodMenu = useMemo(( )=> {
    const items: MenuProps['items'] = (categoryMoodOptions1 || []).map((comp, index) => {
      const selected = selMood.length > 0 && selMood.find((c) => c === comp.label);
      return {
        key: index,
        label: (
        <div className={`${styles.gradeDropDownMenuitem} ${selected ? styles.select : ''}`}>
            {selected ? <IconFill/> : <IconEmpty/>}
            <span>{comp.label}</span>
        </div>
        )
      }
    })
    return items
  }, [selMood])

  const dropDownViewStyle = useMemo(() => {
    const classes = [styles.selectGradeView];
    return classes.join(' ');
  }, [])

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    e.stopPropagation();
    handleChangeKeyword(e.target.value)
  }

  const onKeyDown: KeyboardEventHandler<HTMLInputElement> = (e: any) => {
    if ((e as KeyboardEvent).key === 'Enter') {
      const currentValue = inputRef.current?.value || search;
      handleApplySearch(currentValue);
    }
  };

  const handleSearchClick = () => {
    const currentValue = inputRef.current?.value || search;
    handleApplySearch(currentValue);
  };

  const handleSuggestion = (suggestion: string) => {
    handleChangeKeyword(suggestion);
    inputRef.current?.focus();
  };

  const searchBar = (
        <div className={styles.filterBox}>
          <div className={styles.emptyTitle}>
            <input
              ref={inputRef} id="searchInput" placeholder='Search for a Lesson or Keyword'
              value={search} className={styles.input} type="text"
              onFocus={handleFocus} onBlur={handleBlur}
              onChange={onChange} onKeyDown={onKeyDown} />
          </div>
          <div className={styles.selectViews}>
            <Dropdown menu={{ items: gradeMenu, onClick: selectGrade }} trigger={['click']} placement="bottom" onOpenChange={handleGradeDropdownVisible} open={showGradeMenu}>
              <div className={dropDownViewStyle}>
                <div className={styles.gradeField}>
                    <div className={styles.gradeLabel}>
                      {selGrades.length > 0 ? <div className={styles.badge}/> : null}
                      <RewardIcon width={32} height={32}/><span>{isAUuser ? 'Year' : 'Grade'}</span>
                    </div>
                </div>
                <div className={[styles.dropdownToggle, showGradeMenu ? styles.dropdownToggleClose : styles.dropdownToggleOpen].join(' ')}>
                    <ChevronDown className={styles.icon}/>
                </div>
              </div>
            </Dropdown>
            {!isAUuser ? <Dropdown menu={{ items: lessonTypeMenu, onClick: selectLessonType }} trigger={['click']} placement="bottom" onOpenChange={handleLessonTypeDropdownVisible} open={showLessonTypeMenu}>
              <div className={dropDownViewStyle}>
                <div className={styles.gradeField}>
                  <div className={styles.gradeLabel}>
                    {selLessonTypes.length ? <div className={styles.badge}/> : null}
                    <LordIcon src={IconLessons} colors={{primary: '#F78560', secondary: '#F78560'}} isHover={true} size={32}/>
                    <span>{'Lesson Type'}</span>
                  </div>
                </div>
                <div className={[styles.dropdownToggle, showLessonTypeMenu ? styles.dropdownToggleClose : styles.dropdownToggleOpen].join(' ')}>
                  <ChevronDown className={styles.icon}/>
                </div>
              </div>
            </Dropdown> : null}
            {!isAUuser ? <Dropdown menu={{ items: competencyMenu, onClick: selectCompetency }} trigger={['click']} placement="bottom" onOpenChange={handleCompetencyDropdownVisible} open={showCompetencyMenu}>
              <div className={dropDownViewStyle}>
                <div className={styles.gradeField}>
                    <div className={styles.gradeLabel}>
                    {selCompetencys.length > 0 ? <div className={styles.badge}/> : null}
                      <LampIcon  width={32} height={32}/><span>{'Competency'}</span>
                    </div>
                </div>
                <div className={[styles.dropdownToggle, showCompetencyMenu ? styles.dropdownToggleClose : styles.dropdownToggleOpen].join(' ')}>
                    <ChevronDown className={styles.icon}/>
                </div>
              </div>
            </Dropdown> : <Dropdown menu={{ items: skillMenu, onClick: selectSkill }} trigger={['click']} placement="bottom" onOpenChange={handleSkillDropdownVisible} open={showSkillMenu}>
              <div className={dropDownViewStyle}>
                <div className={styles.gradeField}>
                    <div className={styles.gradeLabel}>
                    {selSkill.length > 0 ? <div className={styles.badge}/> : null}
                      <LampIcon  width={32} height={32}/><span>{'Skill'}</span>
                    </div>
                </div>
                <div className={[styles.dropdownToggle, showSkillMenu ? styles.dropdownToggleClose : styles.dropdownToggleOpen].join(' ')}>
                    <ChevronDown className={styles.icon}/>
                </div>
              </div>
            </Dropdown>}
            {!isAUuser ? <Dropdown menu={{ items: tierMenu, onClick: selectTier }} trigger={['click']} placement="bottom" onOpenChange={handleTierDropdownVisible} open={showTierMenu}>
              <div className={dropDownViewStyle}>
                <div className={styles.gradeField}>
                  <div className={styles.gradeLabel}>
                    {selTiers.length > 0 ? <div className={styles.badge}/> : null}
                    <TierIcon  width={32} height={32}/><span>{'Tier'}</span>
                  </div>
                </div>
                <div className={[styles.dropdownToggle, showTierMenu ? styles.dropdownToggleClose : styles.dropdownToggleOpen].join(' ')}>
                    <ChevronDown className={styles.icon}/>
                </div>
              </div>
            </Dropdown> : <Dropdown menu={{ items: moodMenu, onClick: selectMood }} trigger={['click']} placement="bottom" onOpenChange={handleMoodDropdownVisible} open={showMoodMenu}>
              <div className={dropDownViewStyle}>
                <div className={styles.gradeField}>
                    <div className={styles.gradeLabel}>
                    {selMood.length > 0 ? <div className={styles.badge}/> : null}
                      <LampIcon  width={32} height={32}/><span>{'Mood'}</span>
                    </div>
                </div>
                <div className={[styles.dropdownToggle, showMoodMenu ? styles.dropdownToggleClose : styles.dropdownToggleOpen].join(' ')}>
                    <ChevronDown className={styles.icon}/>
                </div>
              </div>
            </Dropdown>}
            <Button onClick={handleSearchClick} className={styles.searchButton} label={'Search'} size='small' leftIcon={<SearchIcon className={styles.searchIcon} />} />
          </div>
        </div>
  );

  return (
    <div className={styles.filterBanner}>
      <div className={styles.topicFilters}>
        {suggestions?.length ? (
          <div className={styles.searchWithSuggestions}>
            {searchBar}
            <div className={styles.suggestions}>
              <span>Try:</span>
              {suggestions.map((suggestion) => (
                <button
                  key={suggestion}
                  type="button"
                  className={[styles.suggestion, search === suggestion ? styles.suggestionActive : ''].join(' ')}
                  onClick={() => handleSuggestion(suggestion)}
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </div>
        ) : searchBar}
      </div>
    </div>
  )
}

export default SearchLayout